import httpClient from './httpClient';

// Helper function to parse MongoDB ObjectId format
const parseProductData = (products) => {
  if (!Array.isArray(products)) return products;
  
  return products.map(product => ({
    ...product,
    _id: product._id?.$oid || product._id || null,
    created_at: product.created_at?.$date || product.created_at,
    updated_at: product.updated_at?.$date || product.updated_at,
  }));
};

const getProducts = async (page = 1, pageSize = 10, sortBy = 'name', sortOrder = 'asc', filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    sort_by: sortBy,
    sort_order: sortOrder,
    name: filters.name || undefined,
    category: filters.category || undefined,
    product_id: filters.product_id || undefined,
    part_number: filters.part_number || undefined,
    client_ref_number: filters.client_ref_number || undefined,
    // Manufacturing parameter range filters
    tip_diameter_min: filters.tip_diameter_range?.min ? parseFloat(filters.tip_diameter_range.min) : undefined,
    tip_diameter_max: filters.tip_diameter_range?.max ? parseFloat(filters.tip_diameter_range.max) : undefined,
    total_length_min: filters.total_length_range?.min ? parseFloat(filters.total_length_range.min) : undefined,
    total_length_max: filters.total_length_range?.max ? parseFloat(filters.total_length_range.max) : undefined,
    degree_min: filters.degree_range?.min ? parseFloat(filters.degree_range.min) : undefined,
    degree_max: filters.degree_range?.max ? parseFloat(filters.degree_range.max) : undefined,
  };

  const response = await httpClient.get('/api/products', { params });
  const data = response.data;
  
  // Parse product data to handle MongoDB ObjectId format
  if (data.products) {
    data.products = parseProductData(data.products);
  }
  
  return data;
};

const getCategories = async () => {
  const response = await httpClient.get('/api/products/categories');
  return response.data;
};

const getProductStats = async () => {
  const response = await httpClient.get('/api/products/stats');
  return response.data;
};

const searchProducts = async (query, page = 1, pageSize = 20) => {
  const response = await httpClient.post('/api/products/search', {
    query,
    page,
    page_size: pageSize
  });
  const data = response.data;
  
  // Parse product data to handle MongoDB ObjectId format
  if (data.products) {
    data.products = parseProductData(data.products);
  }
  
  return data;
};

const createProduct = async (productData) => {
  const response = await httpClient.post('/api/products', productData);
  return response.data;
};

// Validate product ID and specification combination uniqueness
const validateProductId = async (productId, specification = '', clientRefNumber = '', currentId = null) => {
  if (!productId) {
    return { isValid: false, message: '产品编号不能为空' };
  }

  try {
    const response = await httpClient.post('/api/products/validate', {
      product_id: productId,
      specification: specification || '',
      client_ref_number: clientRefNumber || '',
      current_id: currentId // For edit mode
    });

    return response.data;
  } catch (error) {
    console.error('Error validating product ID, specification, and client reference number:', error);
    // On error, assume valid to not block the user
    return { isValid: true, message: '' };
  }
};

const searchCategories = async (query, limit = 30) => {
  const params = { q: query, limit };
  const response = await httpClient.get('/api/products/categories/search', { params });
  return response.data;
};

const getProduct = async (productId) => {
  const response = await httpClient.get(`/api/products/${productId}`);
  return response.data;
};

const updateProduct = async (productId, productData) => {
  const response = await httpClient.put(`/api/products/${productId}`, productData);
  return response.data;
};

const deleteProduct = async (productId) => {
  const response = await httpClient.delete(`/api/products/${productId}`);
  return response.data;
};

// Advanced search with multiple criteria
const advancedSearch = async (searchCriteria) => {
  const response = await httpClient.post('/api/products/advanced-search', searchCriteria);
  const data = response.data;
  
  // Parse product data to handle MongoDB ObjectId format
  if (data.products) {
    data.products = parseProductData(data.products);
  }
  
  return data;
};

// Get manufacturing terminology suggestions
const getManufacturingTerms = async (category = '', field = '') => {
  try {
    const params = { category, field };
    const response = await httpClient.get('/api/products/manufacturing-terms', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to load manufacturing terms:', error);
    // Return fallback terms based on field
    const fallbackTerms = {
      material: ['钢', '不锈钢', '铝', '铜', '塑料', '陶瓷', '碳纤维'],
      coating: ['镀锌', '阳极氧化', '电镀', '喷涂', '磷化', '发黑'],
      unit: ['支', '个', '件', 'kg', 'g', 'm', 'cm', 'mm', '套', '副'],
      hardness: ['HRC45-50', 'HRC50-55', 'HRC55-60', 'HV200-250', 'HV250-300'],
    };
    return { terms: fallbackTerms[field] || [] };
  }
};

// Enhanced search with manufacturing intelligence
const intelligentSearch = async (query, options = {}) => {
  try {
    const searchParams = {
      query: query.trim(),
      fuzzy: options.fuzzy !== false, // Enable fuzzy search by default
      manufacturing_context: options.manufacturingContext !== false,
      include_suggestions: options.includeSuggestions !== false,
      page: options.page || 1,
      page_size: options.pageSize || 20,
      sort: options.sort || { by: 'relevance', order: 'desc' }
    };
    
    const response = await httpClient.post('/api/products/intelligent-search', searchParams);
    const data = response.data;
    
    // Parse product data
    if (data.products) {
      data.products = parseProductData(data.products);
    }
    
    return data;
  } catch (error) {
    console.error('Intelligent search failed, falling back to basic search:', error);
    // Fallback to basic search
    return await searchProducts(query, options.page, options.pageSize);
  }
};

// Get category statistics for dynamic filtering
const getCategoryStats = async (searchQuery = '') => {
  const params = searchQuery ? { search: searchQuery } : {};
  const response = await httpClient.get('/api/products/categories/stats', { params });
  return response.data;
};

// Get product recommendations for a client
const getRecommendations = async (clientId, limit = 8) => {
  const response = await httpClient.get(`/api/products/recommendations/${encodeURIComponent(clientId)}`, {
    params: { limit }
  });
  const data = response.data;
  
  if (data.recommendations) {
    data.recommendations = parseProductData(data.recommendations);
  }
  
  return data;
};

// Get recent products for a user
const getRecentProducts = async (userId, limit = 10) => {
  const response = await httpClient.get(`/api/products/recent/${encodeURIComponent(userId)}`, {
    params: { limit }
  });
  const data = response.data;
  
  if (data.recent_products) {
    data.recent_products = parseProductData(data.recent_products);
  }
  
  return data;
};

// Get enhanced product details
const getProductDetails = async (productId) => {
  const response = await httpClient.get(`/api/products/${productId}/details`);
  const data = response.data;

  // Parse the enhanced product data
  if (data._id) {
    const parsedData = parseProductData([data]);
    return parsedData[0];
  }

  return data;
};

// Get count of linked documents (SO/PO/quotations) for a product
// Used to show impact summary before product_id changes
const getLinkedDocuments = async (productObjectId) => {
  const response = await httpClient.get(`/api/products/${productObjectId}/linked-documents`);
  return response.data;
};

// Bulk operations for products
const bulkAddToFavorites = async (productIds) => {
  // Mock implementation - in real app would call backend API
  console.log('Adding to favorites:', productIds);
  return { success: true, added: productIds.length };
};

const exportProductList = async (products, format = 'csv') => {
  // Client-side export functionality
  if (format === 'csv') {
    const headers = ['产品编号', '产品名称', '规格', '分类', '客户参考号'];
    const csvContent = [
      headers.join(','),
      ...products.map(product => [
        product.product_id || '',
        `"${product.name || ''}"`,
        `"${product.specification || ''}"`,
        `"${product.category || ''}"`,
        `"${product.client_ref_number || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `products_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    return { success: true, format: 'csv' };
  }
  
  return { success: false, error: 'Unsupported format' };
};

// Single product price history lookup
const searchProductPriceHistory = async (productId, options = {}) => {
  try {
    const {
      currentQuotationId = null,
      page = 1,
      pageSize = 50
    } = options;

    const response = await httpClient.post('/api/products/price-history', {
      product_id: productId,
      current_quotation_id: currentQuotationId,
      page,
      page_size: pageSize
    });
    return response.data;
  } catch (error) {
    console.error('Product price history search error:', error);
    throw error;
  }
};

// Advanced price history search by manufacturing parameters
const searchAdvancedPriceHistory = async (criteria) => {
  try {
    const searchParams = {
      // Tip diameter - exact or range
      tip_diameter: criteria.tipDiameter || undefined,
      tip_diameter_min: criteria.tipDiameterMin || undefined,
      tip_diameter_max: criteria.tipDiameterMax || undefined,
      // Total length - exact or range
      total_length: criteria.totalLength || undefined,
      total_length_min: criteria.totalLengthMin || undefined,
      total_length_max: criteria.totalLengthMax || undefined,
      // Text filters
      category: criteria.category || undefined,
      client_name: criteria.clientName || undefined,
      product_name: criteria.productName || undefined,
      // Pagination
      page: criteria.page || 1,
      page_size: criteria.pageSize || 20,
      sort_by: criteria.sortBy || 'date',
      sort_order: criteria.sortOrder || 'desc'
    };

    // Remove undefined values
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key] === undefined) {
        delete searchParams[key];
      }
    });

    const response = await httpClient.post('/api/products/price-history-advanced', searchParams);
    return response.data;
  } catch (error) {
    console.error('Advanced price history search error:', error);
    throw error;
  }
};

const productService = {
  // Original methods
  getProducts,
  getCategories,
  getProductStats,
  searchProducts,
  createProduct,
  validateProductId,
  searchCategories,
  getProduct,
  updateProduct,
  deleteProduct,

  // New advanced methods
  advancedSearch,
  getCategoryStats,
  getRecommendations,
  getRecentProducts,
  getProductDetails,
  getLinkedDocuments,
  bulkAddToFavorites,
  exportProductList,
  searchProductPriceHistory,
  searchAdvancedPriceHistory,

  // Manufacturing-specific methods
  getManufacturingTerms,
  intelligentSearch,
};

export default productService;
